import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { NgForm, FormControl } from '@angular/forms';
import moment from 'moment';

import { ResourceTypeService, SeoService, PubDateOptionService, TermService } from '../_services';
import { AppState } from '../app.service';
import { DateRangeModel } from '../_interfaces';
import DateFormat from '../_utilities/date.format';
import { BehaviorSubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ProductCheck } from '../_utilities';

declare var dataLayer: any;

@Component({
  selector: 'pq-advance-search',
  templateUrl: './advanced.search.component.html',
  styleUrls: ['./_advanced.search.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AdvancedSearchComponent implements OnInit {
  public defaultProfile: string = 'default';
  public defaultSourceType: string = 'all sources';
  public title: string = 'Advanced Search';
  public dateRangeModel: DateRangeModel = new DateRangeModel();
  public ctrl: FormControl = new FormControl();
  public term: string = '';
  public filteredOptions: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  public limitToFilter: object[] = [];
  public sourceTypes: object[] = [];
  public iseLibrary: boolean = false;
  public lexileScoreRangeError: string = '';
  public suppressLexileScores: boolean = false;
  public suppressSimpleLimiters: boolean = false;
  public showDateRange: boolean = false;
  public dateRangeError: string = '';
  public currencyFilter: boolean = false;
  public currentSourceType: string = this.defaultSourceType;
  public suppressSourceTypes: boolean = false;
  public profile: string;
  public productName: string = this._appState.get('displayname') || '';
  public ariaMessageTxt: string = '';
  public static ariaSearchBoxMessageTxt = `Search suggestions displayed. Use the down arrow key to browse suggestions.`;

  private _hasFromPubDateFilterError: boolean = false;
  private _hasToPubDateFilterError: boolean = false;
  private _lexileErrors: Object = {
    invalid: this._appState.get('lexileInvalidError'),
    range: this._appState.get('lexileRangeError')
  };

  constructor (
    private _router: Router,
    private _sourceType: ResourceTypeService,
    private _seoSvc: SeoService,
    private _pubDateOptionSvc: PubDateOptionService,
    private _appState: AppState,
    private _termSvc: TermService,
    private _productCheck: ProductCheck
  ) {
    this._seoSvc.setTitle(this.title);
  }

  public ngOnInit () {
    if (typeof this._appState.get('current_profile') !== 'undefined') {
      this.profile = this._appState.get('current_profile');
    }

    let profile = this.profile || this.defaultProfile;
    let settings = (typeof this._appState.get('profile') !== 'undefined') && this._appState.get('profile')[profile] && this._appState.get('profile')[profile]['settings']
      ? this._appState.get('profile')[profile]['settings'] : [];

    if (settings && settings.length >= 1) {
      settings = settings[0];
      this._updateFormFieldsFromProfile(settings);
    }

    this.ctrl.valueChanges.pipe(
      switchMap((input: string) => {
        this.ariaMessageTxt = '';
        return this._termSvc.get(input || '', this.profile || '');
      })
    ).subscribe((results: string[]) => {
      this.filteredOptions.next(results);
      this.ariaMessageTxt = AdvancedSearchComponent.ariaSearchBoxMessageTxt;
    });

    this.iseLibrary = this._productCheck.iseLibraryCheck(this._appState.get('current_profile'));
  }

  public handleAutoCompleteOpened () {
    this.ariaMessageTxt = AdvancedSearchComponent.ariaSearchBoxMessageTxt;
  }

  public handleAutoCompleteClosed () {
    this.ariaMessageTxt = '';
  }

  public doSearch (form: NgForm, e: Event) {
    e.preventDefault();
    const formData = form.value;
    const route = this.profile ? this.profile + '/' + 'results' : 'results';
    const query = this.ctrl.value;

    let limit = this.createLimitArray(formData);
    let params = {};
    params['searchType'] = 'advanced';
    params['query'] = query;

    // push search to google analytics
    this.pushSearchToGA(params['query']);

    // add source filter param if form not set to all
    if (formData['source'] !== 'all sources') {
      params['source'] = formData['source'];
    }

    // date range
    params = this._updateDateRangeParams(formData, params);

    // lexile score range
    params = this._updateLexileScoreRangeParams(formData, params);

    // if limit array is populated add limit filter param
    if (limit.length) {
      params['limit'] = limit.join('|');
    }

    // currency filter
    params['currencyfilter'] = this.currencyFilter;
    // only submit the form if there it a query value
    if (query && this.dateRangeError === '' && this.lexileScoreRangeError === '') {
      this._appState.set({ advanceSearchType: true, logSearch: true, searchParams: params });
      this._router.navigate([route], { queryParams: params });
    }
  }

  public onDateRangeChange (e: any) {
    this.dateRangeError = ''; // reset error message
    this.dateRangeModel = new DateRangeModel(); // reset the model

    if (e.currentTarget.value === 'custom') {

      if (this.showDateRange) {
        this.showDateRange = false;
      } else {
        this.showDateRange = true;
      }

    } else {
      this.showDateRange = false;
    }
    this.pushAdvSearchPubDateToGA(e);
  }

  public createLimitArray (formData: Object) {
    let limit = [];

    if (formData['limitprimary']) {
      limit.push('primary');
    }

    if (formData['limitscholarly']) {
      limit.push('scholarly');
    }

    return limit;
  }

  public onSelectFrom (value): void {
    this.dateRangeModel.fromDate = value || undefined;
  }

  public onSelectTo (value): void {
    this.dateRangeModel.toDate = value || undefined;
  }

  private _updateDateRangeParams (formData: Object, params: Object): Object {

    if (formData['pubdateRange'] === 'custom') {

      let fromDate = DateFormat.convertDateToString(this.dateRangeModel.fromDate);
      let toDate = this.dateRangeModel.toDate ?
        DateFormat.convertDateToString(this.dateRangeModel.toDate) : DateFormat.convertDateToString(new Date());

      fromDate = fromDate ?  DateFormat.displayFormat(fromDate) : '';
      toDate = toDate ? DateFormat.displayFormat(toDate) : '';

      this._hasFromPubDateFilterError = (DateFormat.inputValid(fromDate) === false);
      this._hasToPubDateFilterError = (DateFormat.inputValid(toDate) === false);
      this.dateRangeError = '';

      if (!fromDate) {
        this.dateRangeError = this._pubDateOptionSvc.dateErrors['required'];
      } else if (this._hasToPubDateFilterError || this._hasFromPubDateFilterError) {
        this.dateRangeError = this._pubDateOptionSvc.dateErrors['format'];
      } else if (moment(fromDate).isAfter(toDate)) {
        this.dateRangeError = this._pubDateOptionSvc.dateErrors['order'];
      }

      if (this.dateRangeError === '') {
        params['pubstartdate'] = DateFormat.format(fromDate);
        params['pubenddate'] = DateFormat.format(toDate);
      }

    } else  if (formData['pubdateRange'] === '30 days') {
      params['pubstartdate'] = this._pubDateOptionSvc.start30Days;
      params['pubenddate'] = moment().format(this._pubDateOptionSvc.dateFormat);
    } else if (formData['pubdateRange'] === '12 months') {
      params['pubstartdate'] = this._pubDateOptionSvc.start12Months;
      params['pubenddate'] = moment().format(this._pubDateOptionSvc.dateFormat);
    }

    return params;
  }

  private _updateLexileScoreRangeParams (formDate: Object, params: Object): Object {
    const lexileMin: string = formDate['lexilemin'];
    const lexileMax: string = formDate['lexilemax'];

    this.lexileScoreRangeError = '';

    if ((lexileMin && this._isNumeric(lexileMin)) && (lexileMax && this._isNumeric(lexileMax))) {

      if (lexileMin && lexileMax) {
        if ((parseInt(lexileMin, 0) <= parseInt(lexileMax, 0))) {
          params['lexilemin'] = lexileMin;
          params['lexilemax'] = lexileMax;
        } else {
          this.lexileScoreRangeError = this._lexileErrors['range'];
        }

      } else if (lexileMin || lexileMax) {

        if (lexileMin) {
          params['lexilemin'] = lexileMin;
        }

        if (lexileMax) {
          params['lexilemax'] = lexileMax;
        }

      }

    } else if ((lexileMin && this._isNumeric(lexileMin) === false) || (lexileMax && this._isNumeric(lexileMax) === false)) {
      this.lexileScoreRangeError = this._lexileErrors['invalid'];
    }

    if(lexileMin || lexileMax) {
      this.pushAdvSearchLexileScoreToGA(lexileMin, lexileMax);
    }

    return params;
  }

  private _isNumeric (num: string): boolean {
    return !isNaN(parseFloat(num)) && isFinite(parseFloat(num));
  }

  private _updateFormFieldsFromProfile (settings: object): void {
    if (settings['simpleLimiters']) {
      this.limitToFilter = settings['simpleLimiters']['simpleLimiter'] || [];
      this.limitToFilter = this.limitToFilter.sort((a, b) => {
        return parseInt(a['position']) - parseInt(b['position']);
      });

      this.suppressSimpleLimiters = typeof settings['simpleLimiters']['suppress'] === 'undefined' ? true : settings['simpleLimiters']['suppress'];
    }

    if (settings['lexileScores']) {
      this.suppressLexileScores = typeof settings['lexileScores']['suppress'] === 'undefined' ? true : settings['lexileScores']['suppress'];
    }

    if (settings['sourceTypes']) {
      this.suppressSourceTypes = typeof settings['sourceTypes']['suppress'] === 'undefined' ? true : settings['sourceTypes']['suppress'];
      this.sourceTypes = settings['sourceTypes']['sourceType'] || [];
      this.sourceTypes = this.sourceTypes.sort((a, b) => {
        return parseInt(a['position']) - parseInt(b['position']);
      });
      this.sourceTypes.forEach((source, index) => {
        source['id'] = index + 1;
        source['icon'] = 'gr-' + source['key'].toLowerCase();
      });
    }
  }

  public handleSourceTypeSelection (label: string) {
    this._pushAdvSearchSourceTypeToGA(label);
    this.currentSourceType = label.toLowerCase();
  }

  public handleSearchLimitSelection (checked: object, label: string) {
    this._pushAdvSearchLimitToToGA(checked, label);
  }

  public handleCurrencyFilterSelection (checked: object, label: string) {
    this._pushAdvSearchCurrencyFilterToToGA(checked, label);
    this.currencyFilter = (checked) ? true : false;
  }

  private _pushAdvSearchLimitToToGA(checked: object, label: string) {
    if (dataLayer && Array.isArray(dataLayer)) {
      const action = ( checked ) ? 'Select' : 'De-Select';
      dataLayer.push({'event': 'AdvancedSearchLimitToFilter', 'action': action, 'label': label});
    }
  }

  private _pushAdvSearchCurrencyFilterToToGA(checked: object, label: string) {
    if (dataLayer && Array.isArray(dataLayer)) {
      const action = ( checked ) ? 'Select' : 'De-Select';
      dataLayer.push({'event': 'AdvancedSearchCurrencyFilter', 'action': action, 'label': label});
    }
  }

  private _pushAdvSearchSourceTypeToGA(label: string) {
    if (dataLayer && Array.isArray(dataLayer)) {
      dataLayer.push({
        'event': 'AdvancedSearchSourceTypeFilter',
        'action': 'Select',
        'label': label
      });
    }
  }

  private pushSearchToGA(searchKeyword: string) {
    if (dataLayer && dataLayer && Array.isArray(dataLayer)) {
      dataLayer.push({'event': 'SearchTextEntry', 'category': 'Advanced', 'label': searchKeyword});
      dataLayer.push({'event': 'SearchButton', 'category': 'Advanced'});
    }
    this._appState.set({ gaSearchFrom: 'Advanced' });
  }

  private pushAdvSearchPubDateToGA(e: any) {
    if (dataLayer && Array.isArray(dataLayer)) {
      dataLayer.push({
        'event': 'AdvancedSearchPubDateFilter',
        'action': 'Select',
        'label': e.currentTarget.value
      });
    }
  }

  private pushAdvSearchLexileScoreToGA(lexileMin: string, lexileMax: string) {
    if (dataLayer && Array.isArray(dataLayer)) {
      const lexileRange = lexileMin + '-' + lexileMax;
      dataLayer.push({'event': 'AdvancedSearchLexileScoreFilter', 'label': lexileRange});
    }
  }
}
